import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const initialState = {
  createItemError: null,
  publishingListing: null,
  publishListingError: null,
  updateItemError: null,
  showItemError: null,
  uploadImageError: null,
  createItemInProgress: false,
  submittedItemId: null,
  redirectToListing: false,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateItemInProgress: false,
  catalogProducts: [],
  fetchCatalogProductsError: null,
  fetchInProgress: true,
};

// ================== Helpers ================ //

/**
 * Function to update all resources with a specific old type to a new type in a JSON object API response.
 * 
 * @param {Object} jsonObject - The JSON object containing the resources.
 * @param {string} oldType - The old type to be updated.
 * @param {string} newType - The new type to set.
 * @returns {Object} - The updated JSON object.
 */
export function updateResourceType(jsonObject, oldType, newType) {
  // Function to update type in a single item
  const updateTypeInItem = (item) => {
    if (item.type === oldType) {
      item.type = newType;
    }
    // Update relationships if they exist
    if (item.relationships) {
      Object.values(item.relationships).forEach(relationship => {
        if (relationship.data) {
          if (relationship.data.type === oldType) {
            relationship.data.type = newType;
          }
          // Handle relationship data arrays
          if (Array.isArray(relationship.data)) {
            relationship.data.forEach(relItem => {
              if (relItem.type === oldType) {
                relItem.type = newType;
              }
            });
          }
        }
      });
    }
  };

  // Function to update type in an array of items
  const updateTypeInArray = (array) => {
    array.forEach(item => updateTypeInItem(item));
  };

  // Update type in 'data' (object or array)
  if (Array.isArray(jsonObject.data)) {
    updateTypeInArray(jsonObject.data.data);
  } else if (typeof jsonObject.data === 'object' && jsonObject.data !== null) {
    updateTypeInItem(jsonObject.data.data);
  }

  // Update type in 'included' array
  if (Array.isArray(jsonObject.data.included)) {
    updateTypeInArray(jsonObject.data.included);
  }

  return jsonObject;
}

// ================ Async Thunks ================ //

export const requestShowItem = createAsyncThunk(
  'app/EditItemPage/SHOW_ITEM',
  async (actionPayload, { dispatch, extra: sdk }) => {
    try {
      const response = await sdk.newSdk.catalogItems.show({
        ...actionPayload,
        include: ['catalogProduct', 'images'],
      });
      const formatted = updateResourceType(response, 'catalog-product', 'reference-product');
      dispatch(addMarketplaceEntities(formatted));
      return formatted;
    } catch (e) {
      console.log(e);
      throw storableError(e);
    }
  }
);

export const requestFetchCatalogProducts = createAsyncThunk(
  'app/EditItemPage/FETCH_CATALOG_PRODUCTS',
  async (_, { dispatch, extra: sdk }) => {
    try {
      const response = await sdk.newSdk.catalogProducts.query({
        countryId: process.env.REACT_APP_COUNTRY_ID,
        include: ['images'],
      });
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      console.log(e);
      throw storableError(e);
    }
  }
);

export const requestCreateItem = createAsyncThunk(
  'app/EditItemPage/CREATE_ITEM',
  async (data, { dispatch, extra: sdk }) => {
    try {
      const queryParams = { expand: true, include: ['images', 'catalogProduct'] };
      const response = await sdk.newSdk.catalogItems.create(data, queryParams);
      
      dispatch(addMarketplaceEntities(response));
      const id = response.data.data.id.uuid;
      await dispatch(requestShowItem({ id, include: ['images', 'catalogProduct'] }));

      return response;
    } catch (e) {
      log.error(e, 'create-item-draft-failed', { listingData: data });
      throw storableError(e);
    }
  }
);

export const requestImageUpload = createAsyncThunk(
  'app/EditItemPage/UPLOAD_IMAGE',
  async (actionPayload, { extra: sdk }) => {
    const { id, file } = actionPayload;
    try {
      const resp = await sdk.newSdk.images.upload({ file });
      return { id, imageId: resp.data.data.id, file };
    } catch (e) {
      throw { id, error: storableError(e) };
    }
  }
);

export const requestUpdateItem = createAsyncThunk(
  'app/EditItemPage/UPDATE_ITEM',
  async (data, { dispatch, extra: sdk }) => {
    try {
      const { id, catalogProductId, ...restData } = data;
      const response = await sdk.newSdk.catalogItems.update({
        id,
        ...restData,
        include: ['images', 'catalogProduct'],
      });

      await dispatch(requestShowItem({ id, include: ['images', 'catalogProduct'] }));
      return response;
    } catch (e) {
      log.error(e, 'update-item-failed', { listingData: data });
      throw storableError(e);
    }
  }
);

export const requestShowProduct = createAsyncThunk(
  'app/EditItemPage/SHOW_PRODUCT',
  async (actionPayload, { dispatch, extra: sdk }) => {
    try {
      const response = await sdk.newSdk.catalogProducts.show(actionPayload);

      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      throw storableError(e);
    }
  }
);

export const loadData = params => (dispatch) => {
  dispatch(resetPageState());
  
  const { id, type } = params;
  if (type === 'new') {
    return Promise.all([dispatch(fetchCurrentUser()), dispatch(requestFetchCatalogProducts())]);
  }
  return Promise.all([
    dispatch(requestShowItem({ id })),
    dispatch(fetchCurrentUser()),
    dispatch(requestFetchCatalogProducts()),
  ]);
};

// ================ Slice ================ //

const editItemPageSlice = createSlice({
  name: 'app/EditItemPage',
  initialState,
  reducers: {
    markTabUpdated: (state, action) => {
      state.updatedTab = action.payload;
    },
    clearUpdatedTab: state => {
      state.updatedTab = null;
      state.updateItemError = null;
    },
    resetPageState: () => initialState,
    updateImageOrder: (state, action) => {
      state.imageOrder = action.payload.imageOrder;
    },
    removeItemImage: (state, action) => {
      const rawId = action.payload?.imageId || action.payload;
      const uuid = typeof rawId === 'object' && rawId !== null ? rawId.uuid || rawId.id : rawId;

      if (!state.images[uuid]) {
        if (!state.removedImageIds.includes(uuid)) {
          state.removedImageIds.push(uuid);
        }
      }
      delete state.images[uuid];
      state.imageOrder = state.imageOrder.filter(i => i !== uuid);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(requestCreateItem.pending, state => {
        state.createItemInProgress = true;
        state.createItemError = null;
        state.submittedItemId = null;
        state.listingDraft = null;
      })
      .addCase(requestCreateItem.fulfilled, (state, action) => {
        state.createItemInProgress = false;
        state.submittedItemId = action.payload.data.data.id;
        state.listingDraft = action.payload.data;
      })
      .addCase(requestCreateItem.rejected, (state, action) => {
        state.createItemInProgress = false;
        state.createItemError = action.error;
      })

      .addCase(requestUpdateItem.pending, state => {
        state.updateItemInProgress = true;
        state.updateItemError = null;
      })
      .addCase(requestUpdateItem.fulfilled, state => {
        state.updateItemInProgress = false;
      })
      .addCase(requestUpdateItem.rejected, (state, action) => {
        state.updateItemInProgress = false;
        state.updateItemError = action.error;
      })

      .addCase(requestShowItem.pending, state => {
        state.showItemError = null;
      })
      .addCase(requestShowItem.rejected, (state, action) => {
        state.showItemError = action.error;
        state.redirectToListing = false;
      })

      .addCase(requestFetchCatalogProducts.pending, state => {
        state.fetchCatalogProductsError = null;
      })
      .addCase(requestFetchCatalogProducts.fulfilled, (state, action) => {
        state.catalogProducts = action.payload.data.data;
        state.fetchCatalogProductsError = null;
      })
      .addCase(requestFetchCatalogProducts.rejected, (state, action) => {
        state.fetchCatalogProductsError = action.error;
      })

      .addCase(requestImageUpload.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.images[id] = { id, file };
        state.imageOrder.push(id);
        state.uploadImageError = null;
      })
      .addCase(requestImageUpload.fulfilled, (state, action) => {
        const { id, imageId, file } = action.payload;
        state.images[id] = { id, imageId, file };
      })
      .addCase(requestImageUpload.rejected, (state, action) => {
        const id = action.error.id;
        state.imageOrder = state.imageOrder.filter(i => i !== id);
        delete state.images[id];
        state.uploadImageError = action.error.error;
      });
  },
});

export const {
  markTabUpdated,
  clearUpdatedTab,
  updateImageOrder,
  removeItemImage,
  resetPageState,
} = editItemPageSlice.actions;

export default editItemPageSlice.reducer;
